import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
  useCreatePromocodeMutation,
  useLazySearchUsersQuery,
} from "../../app/admin/api";

interface CreatePromocodeModalProps {
  open: boolean;
  onClose: () => void;
}

const CreatePromocodeModal: React.FC<CreatePromocodeModalProps> = ({
  open,
  onClose,
}) => {
  const [createPromocode, { isLoading }] = useCreatePromocodeMutation();
  const { t } = useTranslation("app");
  const [triggerSearch, { isFetching }] = useLazySearchUsersQuery();

  const [formData, setFormData] = useState({
    userId: "",
    cases: "",
    price: "",
  });

  const [userOptions, setUserOptions] = useState<
    { id: string | number; label: string }[]
  >([]);

  const handleSearch = async (q: string) => {
    try {
      const result = await triggerSearch({ query: q, limit: 10 }).unwrap();
      setUserOptions(result);
    } catch {
      setUserOptions([]);
    }
  };

  const handleInputChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleClose = () => {
    setFormData({
      userId: "",
      cases: "",
      price: "",
    });
    onClose();
  };

  const handleSubmit = async () => {
    try {
      // Validate form data
      if (!formData.userId || !formData.cases || !formData.price) {
        toast.error(
          t(
            "admin.promocodeModal.validation.fillAll",
            "Please fill in all fields"
          )
        );
        return;
      }

      const userId = parseInt(formData.userId);
      const cases = parseInt(formData.cases);
      const price = parseInt(formData.price);

      if (isNaN(userId) || isNaN(cases) || isNaN(price)) {
        toast.error(
          t(
            "admin.promocodeModal.validation.invalidNumbers",
            "Please enter valid numbers"
          )
        );
        return;
      }

      if (userId <= 0 || cases <= 0 || price <= 0) {
        toast.error(
          t(
            "admin.promocodeModal.validation.valuesGtZero",
            "Values must be greater than 0"
          )
        );
        return;
      }

      await createPromocode({
        userId,
        cases,
        price,
        currency: "KZT",
      }).unwrap();

      toast.success(
        t("admin.promocodeModal.success", "Promocode created successfully!")
      );
      handleClose();
    } catch (error) {
      console.error("Error creating promocode:", error);
      toast.error(
        t("admin.promocodeModal.error", "Failed to create promocode")
      );
    }
  };

  return (
    <Dialog maxWidth="sm" open={open} fullWidth onClose={handleClose}>
      <DialogTitle>
        <Typography component="div" variant="h6">
          {t("admin.promocodeModal.title", "Create Promocode")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Autocomplete
            filterOptions={(x) => x}
            getOptionLabel={(
              option: { id: string | number; label: string } | string
            ) => (typeof option === "string" ? option : option.label)}
            loading={isFetching}
            options={userOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                helperText={t(
                  "admin.promocodeModal.userIdHelp",
                  "Enter the user ID for whom to create the promocode"
                )}
                label={t("admin.promocodeModal.userIdLabel", "User ID")}
              />
            )}
            onChange={(_, option) => {
              setFormData((prev) => ({
                ...prev,
                userId:
                  option && typeof option !== "string"
                    ? String((option as { id: string | number }).id)
                    : "",
              }));
            }}
            onInputChange={(_, value) => {
              if (value && value.length >= 2) {
                handleSearch(value);
              } else {
                setUserOptions([]);
              }
            }}
          />
          <TextField
            helperText={t(
              "admin.promocodeModal.casesHelp",
              "Number of cases to include in the promocode"
            )}
            label={t("admin.promocodeModal.casesLabel", "Cases")}
            type="number"
            value={formData.cases}
            fullWidth
            required
            onChange={handleInputChange("cases")}
          />
          <TextField
            helperText={t("admin.promocodeModal.priceHelp", "Price in KZT")}
            label={t("admin.promocodeModal.priceLabel", "Price")}
            type="number"
            value={formData.price}
            fullWidth
            required
            onChange={handleInputChange("price")}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={handleClose}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button disabled={isLoading} variant="contained" onClick={handleSubmit}>
          {isLoading
            ? t("admin.promocodeModal.creating", "Creating...")
            : t("admin.promocodeModal.create", "Create Promocode")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePromocodeModal;
